import React from 'react';
import { ClipboardList, IndianRupee, Users } from 'lucide-react';
import VisitReportsTable from './VisitReportsTable';

const DATE_FILTERS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week', label: 'This Week' },
  { key: 'lastweek', label: 'Last Week' },
  { key: 'month', label: 'This Month' },
  { key: 'lastmonth', label: 'Last Month' },
  { key: '3months', label: 'Last 3 Months' },
  { key: '6months', label: 'Last 6 Months' },
  { key: 'all', label: 'All' },
  { key: 'custom', label: 'Custom' },
];

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

function Kpi({ label, value, sub, icon, color, bold }) {
  return (
    <div className="bg-white border rounded-xl p-3 shadow-sm relative overflow-hidden">
      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: color }} />
      <div className="ml-1.5 flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg flex items-center justify-center text-white" style={{ background: color }}>
          {icon}
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">{label}</div>
          <div className={`text-base ${bold ? 'font-extrabold' : 'font-bold'} text-zinc-900 leading-none mt-0.5 truncate`} style={{ fontFamily: 'Manrope' }}>{value}</div>
          {sub && <div className="text-[10px] text-zinc-400 mt-1 truncate">{sub}</div>}
        </div>
      </div>
    </div>
  );
}

/** Filters + KPI strip + visit table for reception / FIC reports. */
export default function VisitReportsPanel({
  filter, setFilter, from, setFrom, to, setTo,
  doctorFilter, setDoctorFilter, statusFilter, setStatusFilter,
  doctors, rows, totals, data, loading, brand,
}) {
  return (
    <>
      <style>{`@media print { .no-print { display: none !important; } body { background: white; } }`}</style>
      <div className="space-y-5" data-testid="reception-reports">
        <div className="bg-white border rounded-2xl p-4 shadow-sm space-y-3 no-print">
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="report-date-filter">
            {DATE_FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition ${
                  filter === f.key
                    ? 'bg-zinc-900 text-white border-zinc-900'
                    : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400'
                }`}
                data-testid={`report-filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          {filter === 'custom' && (
            <div className="flex items-center gap-2 flex-wrap">
              <label className="text-xs text-zinc-500">From</label>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                className="border border-zinc-200 rounded-md px-2 py-1 text-sm" data-testid="report-from" />
              <label className="text-xs text-zinc-500">To</label>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                className="border border-zinc-200 rounded-md px-2 py-1 text-sm" data-testid="report-to" />
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            {doctors.length > 1 && (
              <select value={doctorFilter} onChange={(e) => setDoctorFilter(e.target.value)}
                className="border border-zinc-200 rounded-md px-2 py-1 text-sm" data-testid="report-doctor-filter">
                <option value="">All doctors</option>
                {doctors.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            )}
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-zinc-200 rounded-md px-2 py-1 text-sm" data-testid="report-status-filter">
              <option value="">All statuses</option>
              <option value="waiting">Waiting</option>
              <option value="in_consult">In Consult</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Kpi label="Visits" value={totals.visits || 0} icon={<Users />} color={brand} />
          <Kpi label="Consult ₹" value={fmt(totals.consultation_total)} icon={<IndianRupee />} color="#0891b2" />
          <Kpi label="Pharmacy ₹" value={fmt(totals.pharmacy_total)} icon={<ClipboardList />} color="#7c3aed" />
          <Kpi label="GST ₹" value={fmt(totals.pharmacy_gst)} icon={<ClipboardList />} color="#f59e0b" />
          <Kpi label="Total ₹" value={fmt(totals.grand_total)} bold icon={<IndianRupee />} color="#dc2626" />
        </div>

        <VisitReportsTable filter={filter} data={data} rows={rows} totals={totals} loading={loading} />

        {totals.by_doctor && Object.keys(totals.by_doctor).length > 0 && (
          <div className="bg-white border rounded-2xl p-5 shadow-sm">
            <h3 className="text-sm font-bold text-zinc-900 mb-3" style={{ fontFamily: 'Manrope' }}>Visits by doctor</h3>
            <div className="space-y-1.5">
              {Object.entries(totals.by_doctor).sort((a, b) => b[1] - a[1]).map(([name, n]) => (
                <div key={name} className="flex items-center gap-3">
                  <div className="text-sm text-zinc-800 min-w-[140px] font-semibold">{name}</div>
                  <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${(n / (totals.visits || 1)) * 100}%`, background: brand }} />
                  </div>
                  <div className="text-xs text-zinc-500 min-w-[80px] text-right">{n} visit{n === 1 ? '' : 's'}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
